"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import type { HomeTestimonialsSectionContent } from "@/features/home/types/home-page-content";

interface HomeTestimonialsSectionProps {
  content: HomeTestimonialsSectionContent;
}

const ROTATE_INTERVAL_MS = 4500;

/**
 * Auto-rotating testimonial stack.
 *
 * Three quotes are visible at once: the previous one sits at the top (muted +
 * blurred), the active one is centered and sharp, and the next one waits at the
 * bottom (muted + blurred). On each tick the active quote lifts away while the
 * next quote zooms up into the center and resolves into focus.
 */
export function HomeTestimonialsSection({ content }: HomeTestimonialsSectionProps) {
  const items = content.items;
  const total = items.length;
  const [activeIndex, setActiveIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const advance = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % total);
  }, [total]);

  const restartTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(advance, ROTATE_INTERVAL_MS);
  }, [advance]);

  useEffect(() => {
    if (total <= 1) {
      return;
    }
    restartTimer();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [restartTimer, total]);

  const goTo = (index: number) => {
    setActiveIndex(index);
    restartTimer();
  };

  // Signed distance from the active slot, wrapped to the nearest direction so
  // the carousel can travel either way without a long jump on wrap-around.
  const slotOffset = (index: number) => {
    let diff = index - activeIndex;
    if (diff > total / 2) {
      diff -= total;
    } else if (diff < -total / 2) {
      diff += total;
    }
    return diff;
  };

  return (
    <section
      id={content.id}
      className="relative flex min-h-screen w-full items-center overflow-hidden bg-background py-24 sm:py-28"
    >
      {/* Faint concentric rings echoing the brand mark on the right edge */}
      <div className="pointer-events-none absolute top-1/2 right-[-12rem] -translate-y-1/2 select-none sm:right-[-8rem]">
        {[0, 1, 2, 3].map((ring) => (
          <span
            key={ring}
            className="absolute rounded-full border border-primary/15"
            style={{
              width: `${20 + ring * 9}rem`,
              height: `${20 + ring * 9}rem`,
              left: `${-(10 + ring * 4.5)}rem`,
              top: `${-(10 + ring * 4.5)}rem`,
            }}
          />
        ))}
      </div>

      {/* Brand logo anchored to the far right, gently faded into the canvas */}
      <div className="pointer-events-none absolute top-1/2 -right-16 hidden -translate-y-1/2 opacity-5 sm:block lg:right-12">
        <Image
          src={content.logoUrl}
          alt={content.logoAlt}
          width={160}
          height={160}
          className="h-28 w-28 object-contain lg:h-20 lg:w-20"
        />
      </div>

      <div className="relative mx-auto flex h-full w-full max-w-7xl flex-col px-6 lg:px-8">
        <span className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">
          {content.eyebrow}
        </span>

        <div className="relative mt-10 flex flex-1 items-center gap-6 sm:mt-14 sm:gap-12">
          {/* Oversized quote glyph */}
          <Quote
            aria-hidden
            className="hidden h-20 w-20 flex-shrink-0 rotate-180 fill-primary/10 text-primary/10 sm:block lg:h-32 lg:w-32"
            strokeWidth={0}
          />

          {/* Vertical testimonial stage */}
          <div className="relative h-[32rem] w-full max-w-3xl sm:h-[40rem]">
            {items.map((item, index) => {
              const offset = slotOffset(index);
              const isActive = offset === 0;
              const isAdjacent = Math.abs(offset) === 1;

              return (
                <motion.figure
                  key={item.id}
                  aria-hidden={!isActive}
                  className="absolute inset-x-0 top-1/2 mx-auto flex flex-col items-start"
                  initial={false}
                  animate={{
                    y: `calc(-50% + ${offset * 13}rem)`,
                    scale: isActive ? 1 : 0.65,
                    opacity: isActive ? 1 : isAdjacent ? 0.32 : 0,
                    filter: isActive ? "blur(0px)" : "blur(2px)",
                  }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    pointerEvents: isActive ? "auto" : "none",
                    transformOrigin: "left center",
                  }}
                >
                  <blockquote
                    className={cn(
                      "max-full leading-tight font-semibold tracking-tight text-balance transition-colors",
                      isActive
                        ? "text-3xl text-foreground sm:text-5xl lg:text-6xl"
                        : "text-2xl text-muted-foreground sm:text-4xl",
                    )}
                  >
                    {item.quote}
                  </blockquote>

                  {isActive && (item.author || item.role) && (
                    <figcaption className="mt-5 flex items-center gap-2 text-sm text-muted-foreground">
                      {item.author && (
                        <span className="font-semibold text-foreground">{item.author}</span>
                      )}
                      {item.author && item.role && (
                        <span className="h-1 w-1 rounded-full bg-muted-foreground/50" />
                      )}
                      {item.role && <span>{item.role}</span>}
                    </figcaption>
                  )}
                </motion.figure>
              );
            })}
          </div>
        </div>

        {/* Pagination dots */}
        <div className="mt-12 flex items-center gap-2.5 sm:ml-[6.5rem] lg:ml-[8.5rem]">
          {items.map((item, index) => {
            const isActive = index === activeIndex;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => goTo(index)}
                aria-label={`Show testimonial ${index + 1}`}
                aria-current={isActive}
                className={cn(
                  "h-2 rounded-full transition-all duration-500",
                  isActive ? "w-7 bg-primary" : "w-2 bg-primary/25 hover:bg-primary/45",
                )}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}
